/**
 * mentionUtils.ts — Consolidated mention utilities for canvas-v2
 *
 * Provides shared helpers for building mention candidate lists from
 * canvas nodes. Used by RichTextBox (@ mention dropdown) and
 * ReferenceSelector (asset grid).
 *
 * Node data access is narrowed through generated node data helpers.
 * Port name mismatches are caught at compile time.
 */

import {
  getTypedNodeData,
  isTypedReactFlowNode,
  type UntypedReactFlowNode,
} from '@ponpon/api-client'
import { nodeSchemas } from '../../nodeRegistry'
import { extractNodeAssetInfo } from '../../../../utils/buildDownloadFilename'
import type { VideoHistoryEntry } from './variantOrdinals'

// ─── Types ───────────────────────────────────────────────────────────────────

export interface MentionCandidate {
  id: string
  type: string
  label: string
  icon: string
  thumbnailPath?: string
}

export type MediaType = 'image' | 'video' | 'audio'

export interface CanvasAssetCandidate {
  nodeId: string
  nodeType: string
  label: string
  icon: string
  assetPath: string
  mediaType: MediaType
  /** Variant history for nodes that have one (video / image / audio generators, designers, sound expert). */
  variantHistory?: VideoHistoryEntry[]
  /** Picked variant index inside variantHistory. */
  pickedIndex?: number
}

type MentionNode = UntypedReactFlowNode

export interface CanvasAssetEdge {
  source: string
  sourceHandle?: string | null
  target: string
  targetHandle?: string | null
  data?: {
    toIndex?: number
  } | null
}

export interface CanvasAssetEdgeUpdate {
  source: string
  sourceHandle: string
  toIndex: number
}

// ─── Node Image Path Extractors ──────────────────────────────────────────────

function getImagePath(node: MentionNode): string | undefined {
  if (isTypedReactFlowNode(node, 'characterDesigner')) {
    const main = node.data.out_character_sheet?.images?.main
    if (main?.history?.length) {
      return main.history[main.picked_index ?? 0]?.result?.local_path
    }
    return node.data.out_image
  }

  if (isTypedReactFlowNode(node, 'locationDesigner')) {
    const main = node.data.out_location_image?.images?.main
    if (main?.history?.length) {
      return main.history[main.picked_index ?? 0]?.result?.local_path
    }
    return node.data.out_image
  }

  if (isTypedReactFlowNode(node, 'objectDesigner')) {
    const main = node.data.out_object_image?.images?.main
    if (main?.history?.length) {
      return main.history[main.picked_index ?? 0]?.result?.local_path
    }
    return node.data.out_image
  }

  if (isTypedReactFlowNode(node, 'imageGenerator')) {
    if (node.data.out_image) return node.data.out_image
    const gen = node.data.out_generated_image
    if (gen?.history?.length) {
      return gen.history[gen.picked_index ?? 0]?.result?.local_path
    }
    return undefined
  }

  if (isTypedReactFlowNode(node, 'image')) {
    return node.data.out_image
  }

  if (isTypedReactFlowNode(node, 'storyboardDesigner')) {
    const grid = node.data.out_storyboard_grid
    if (grid?.length) return grid[grid.length - 1]
    return undefined
  }

  if (isTypedReactFlowNode(node, 'storyboardNoEnhanceDesigner')) {
    return node.data.out_storyboard_grid || undefined
  }

  return undefined
}

const IMAGE_NODE_TYPES = new Set(['characterDesigner', 'locationDesigner', 'objectDesigner', 'imageGenerator', 'image'])

// ─── Node Video Path Extractors ──────────────────────────────────────────────

function getVideoPath(node: MentionNode): string | undefined {
  if (isTypedReactFlowNode(node, 'video')) {
    return node.data.out_video
  }

  if (isTypedReactFlowNode(node, 'videoGenerator')) {
    if (node.data.out_video) return node.data.out_video
    const gen = node.data.out_generated_video
    const pickedPath = gen?.history?.[gen.picked_index ?? 0]?.result?.local_path
    if (pickedPath) return pickedPath
    return undefined
  }

  if (isTypedReactFlowNode(node, 'videoEditor')) {
    return node.data.out_video
  }

  return undefined
}

const VIDEO_NODE_TYPES = new Set(['video', 'videoGenerator', 'videoEditor'])

// ─── Node Audio Path Extractors ──────────────────────────────────────────────

function getAudioPath(node: MentionNode): string | undefined {
  if (isTypedReactFlowNode(node, 'audio')) {
    return node.data.out_audio
  }

  if (isTypedReactFlowNode(node, 'soundExpert')) {
    if (node.data.out_audio) return node.data.out_audio
    const generated = node.data.in_generated_audio
    if (generated?.history?.length) {
      const pickedIndex = generated.picked_index ?? 0
      return generated.history[pickedIndex]?.result?.local_path
    }
    return undefined
  }

  return undefined
}

const AUDIO_NODE_TYPES = new Set(['audio', 'soundExpert'])

export function getAudioAssetPath(
  nodeType: string,
  data: object | undefined,
): string | undefined {
  if (!data) return undefined

  const soundExpertData = getTypedNodeData({ type: nodeType, data }, 'soundExpert')
  if (soundExpertData) {
    if (soundExpertData.out_audio) return soundExpertData.out_audio
    const generated = soundExpertData.in_generated_audio
    if (generated?.history?.length) {
      const pickedIndex = generated.picked_index ?? 0
      return generated.history[pickedIndex]?.result?.local_path
    }
    return undefined
  }

  const audioData = getTypedNodeData({ type: nodeType, data }, 'audio')
  if (audioData) return audioData.out_audio

  return undefined
}

// ─── Core Functions ──────────────────────────────────────────────────────────

function compactLabel(value: string): string {
  return value.length > 30 ? value.slice(0, 30) + '...' : value
}

function firstLabel(...values: Array<string | undefined>): string | undefined {
  for (const value of values) {
    const trimmed = value?.trim()
    if (trimmed) return trimmed
  }
  return undefined
}

export function getNodeLabel(node: MentionNode): string {
  switch (node.type) {
    case 'characterDesigner': {
      if (isTypedReactFlowNode(node, 'characterDesigner')) {
        const label = firstLabel(node.data.in_name, node.data.label)
        if (label) return label
      }
      break
    }
    case 'imageGenerator': {
      if (isTypedReactFlowNode(node, 'imageGenerator')) {
        const label = firstLabel(node.data.in_name, node.data.label)
        if (label) return label
        const prompt = firstLabel(node.data.in_prompt)
        if (prompt) return compactLabel(prompt)
      }
      break
    }
    case 'locationDesigner': {
      if (isTypedReactFlowNode(node, 'locationDesigner')) {
        const label = firstLabel(node.data.in_name, node.data.label)
        if (label) return label
      }
      break
    }
    case 'nodeGroup':
    case 'shortVideoGroup':
    case 'webtoonGroup': {
      if (
        isTypedReactFlowNode(node, 'nodeGroup') ||
        isTypedReactFlowNode(node, 'shortVideoGroup') ||
        isTypedReactFlowNode(node, 'webtoonGroup')
      ) {
        const label = firstLabel(node.data.in_label, node.data.label)
        if (label) return label
      }
      break
    }
    case 'objectDesigner': {
      if (isTypedReactFlowNode(node, 'objectDesigner')) {
        const label = firstLabel(node.data.in_name, node.data.label)
        if (label) return label
      }
      break
    }
    case 'scriptWriter': {
      if (isTypedReactFlowNode(node, 'scriptWriter')) {
        const label = firstLabel(node.data.in_name, node.data.label)
        if (label) return label
      }
      break
    }
    case 'soundExpert': {
      if (isTypedReactFlowNode(node, 'soundExpert')) {
        const label = firstLabel(node.data.in_name, node.data.label)
        if (label) return label
      }
      break
    }
    case 'image': {
      if (isTypedReactFlowNode(node, 'image')) {
        const label = firstLabel(node.data.label, node.data.in_file_name)
        if (label) return label
      }
      break
    }
    case 'video': {
      if (isTypedReactFlowNode(node, 'video')) {
        const label = firstLabel(node.data.label, node.data.in_file_name)
        if (label) return label
      }
      break
    }
    case 'audio': {
      if (isTypedReactFlowNode(node, 'audio')) {
        const label = firstLabel(node.data.label, node.data.in_file_name)
        if (label) return label
      }
      break
    }
    case 'videoGenerator': {
      if (isTypedReactFlowNode(node, 'videoGenerator')) {
        const label = firstLabel(node.data.in_name, node.data.label)
        if (label) return label
      }
      break
    }
    case 'webtoonPanelGenerator': {
      if (isTypedReactFlowNode(node, 'webtoonPanelGenerator')) {
        const label = firstLabel(node.data.in_name, node.data.label)
        if (label) return label
      }
      break
    }
  }

  const schema = node.type ? nodeSchemas[node.type] : undefined
  if (schema) return schema.label
  return node.type ?? node.id
}

export function getNodeIcon(nodeType: string): string {
  const schema = nodeSchemas[nodeType]
  return schema?.icon ?? ''
}

export function getNodeThumbnailPath(node: MentionNode): string | undefined {
  if (!IMAGE_NODE_TYPES.has(node.type ?? '')) return undefined
  return getImagePath(node)
}

export function buildCandidates(nodes: MentionNode[]): MentionCandidate[] {
  return nodes
    .filter((node) => node.type !== 'director')
    .map((node) => ({
      id: node.id,
      type: node.type ?? '',
      label: getNodeLabel(node),
      icon: getNodeIcon(node.type ?? ''),
      thumbnailPath: getNodeThumbnailPath(node),
    }))
}

export function buildAssetCandidates(
  nodes: MentionNode[],
  excludeNodeId?: string
): CanvasAssetCandidate[] {
  const results: CanvasAssetCandidate[] = []

  for (const node of nodes) {
    if (node.id === excludeNodeId) continue
    const nodeType = node.type ?? ''

    if (IMAGE_NODE_TYPES.has(nodeType)) {
      const path = getImagePath(node)
      if (path) {
        results.push({
          nodeId: node.id,
          nodeType,
          label: getNodeLabel(node),
          icon: getNodeIcon(nodeType),
          assetPath: path,
          mediaType: 'image',
        })
      }
    }

    if (VIDEO_NODE_TYPES.has(nodeType)) {
      const path = getVideoPath(node)
      if (path) {
        results.push({
          nodeId: node.id,
          nodeType,
          label: getNodeLabel(node),
          icon: getNodeIcon(nodeType),
          assetPath: path,
          mediaType: 'video',
        })
      }
    }

    if (AUDIO_NODE_TYPES.has(nodeType)) {
      const path = getAudioPath(node)
      if (path) {
        results.push({
          nodeId: node.id,
          nodeType,
          label: getNodeLabel(node),
          icon: getNodeIcon(nodeType),
          assetPath: path,
          mediaType: 'audio',
        })
      }
    }
  }

  return results
}

function compareTargetIndex(left: number | undefined, right: number | undefined): number {
  const normalizedLeft = left ?? Number.MAX_SAFE_INTEGER
  const normalizedRight = right ?? Number.MAX_SAFE_INTEGER
  return normalizedLeft - normalizedRight
}

export function buildAssetReferencesFromEdges(
  nodes: MentionNode[],
  edges: CanvasAssetEdge[],
  targetId: string,
  targetHandle: string,
  mediaType: MediaType,
): CanvasAssetCandidate[] {
  const candidatesByNodeId = new Map(
    buildAssetCandidates(nodes, targetId)
      .filter((candidate) => candidate.mediaType === mediaType)
      .map((candidate) => [candidate.nodeId, candidate]),
  )

  return edges
    .filter((edge) => edge.target === targetId && edge.targetHandle === targetHandle)
    .sort((left, right) => compareTargetIndex(left.data?.toIndex, right.data?.toIndex))
    .map((edge) => candidatesByNodeId.get(edge.source))
    .filter((candidate): candidate is CanvasAssetCandidate => candidate !== undefined)
}

export function buildImageReferenceEdgeUpdates(
  refs: Array<Pick<CanvasAssetCandidate, 'nodeId' | 'nodeType' | 'assetPath'>>,
): CanvasAssetEdgeUpdate[] {
  const invalid = refs.find((ref) => !ref.nodeId || ref.nodeId.startsWith('ref-'))
  if (invalid) {
    throw new Error(
      `Reference selector cannot save ${invalid.assetPath} without a backing canvas node edge`,
    )
  }

  return refs.map((ref, index) => ({
    source: ref.nodeId,
    sourceHandle: getImageOutputPort(ref.nodeType),
    toIndex: index,
  }))
}

/**
 * Build ALL downloadable asset candidates from canvas nodes.
 * Unlike buildAssetCandidates (which returns one asset per node),
 * this returns multiple assets per node (e.g. all storyboard panel images).
 */
export function buildDownloadCandidates(
  nodes: MentionNode[]
): CanvasAssetCandidate[] {
  const results: CanvasAssetCandidate[] = []

  for (const node of nodes) {
    const nodeType = node.type ?? ''
    const label = getNodeLabel(node)
    const icon = getNodeIcon(nodeType)

    // Single dispatcher: returns mediaType + assetPath + variantHistory + pickedIndex.
    // Shared with per-node header download handlers so bulk + header agree on filenames.
    const info = extractNodeAssetInfo(node)
    if (info) {
      results.push({
        nodeId: node.id,
        nodeType,
        label,
        icon,
        assetPath: info.assetPath,
        mediaType: info.mediaType,
        variantHistory: info.variantHistory,
        pickedIndex: info.pickedIndex,
      })
    }
  }

  return results
}

export function getImageOutputPort(nodeType: string): string {
  const schema = nodeSchemas[nodeType]
  if (!schema) return 'out_image'
  const hasImagePath = schema.outputs?.some((p: { id: string }) => p.id === 'out_image')
  if (hasImagePath) return 'out_image'
  const port = schema.outputs?.find((p: { type: string }) => p.type === 'image')
  return port?.id ?? 'out_image'
}

export function getVideoOutputPort(nodeType: string): string {
  if (nodeType === 'videoGenerator') return 'out_video'
  if (nodeType === 'video') return 'out_video'
  if (nodeType === 'videoEditor') return 'out_video'

  const schema = nodeSchemas[nodeType]
  const preferred = schema?.outputs?.find((p: { id: string }) => p.id === 'out_video')
  if (preferred) return preferred.id
  const port = schema?.outputs?.find((p: { type: string }) => p.type === 'video')
  return port?.id ?? 'out_video'
}

export function getAudioOutputPort(nodeType: string): string {
  if (nodeType === 'soundExpert') return 'out_audio'
  if (nodeType === 'audio') return 'out_audio'

  const schema = nodeSchemas[nodeType]
  const preferred = schema?.outputs?.find((p: { id: string }) => p.id === 'out_audio')
  if (preferred) return preferred.id
  const port = schema?.outputs?.find((p: { type: string }) => p.type === 'audio')
  return port?.id ?? 'out_audio'
}
